import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, DepartmentDTO, DepartmentFilterDTO, PeopleDTO, PeopleFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { DepartmentMaintenance } from './DepartmentMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { access } from 'fs';
import { useTranslation } from 'react-i18next';

export const Department: React.FC = () => {

    const [responsibleList, setResposibleList] = useState<PeopleDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [departmentList, setDepartmentList] = useState<DepartmentDTO[]>([]);
    const [departmentFilterDTO, setDepartmentFilterDTO] = useState<DepartmentFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setDepartmentFilterDTO({
            ...departmentFilterDTO,
            name: '',
            companyId: ''
        } as DepartmentFilterDTO);
    }, []);

    useEffect(() => {
        handleDepartmentGetAllViewAsync();
        handleCompanyGetAll();

        handleGetPermission(ListId.corpsphere.departmentId, setAccessRolePermissionDTO);

    }, [departmentFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleDepartmentGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleResposibleGetAll();
    },[departmentFilterDTO?.companyId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleResposibleGetAll = useCallback(async () => {
        if(departmentFilterDTO?.companyId == '')
            return;

        const peopleFilterDTO: PeopleFilterDTO = {
            companyId: departmentFilterDTO?.companyId,
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.people', peopleFilterDTO, setLoading, setResposibleList, showMessage);
    }, [departmentFilterDTO?.companyId]);
    
    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleDepartmentGetAllViewAsync = useCallback(async () => {
        if (departmentFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('corpsphere.department', departmentFilterDTO, setActionExecuted, setLoading, setDepartmentList, showMessage, true);
    }, [departmentFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setDepartmentFilterDTO({
            ...departmentFilterDTO,
            name: '',
            responsibleId: '',
            companyId: ''
        } as DepartmentFilterDTO);
    };

    function handleSearchClick() {
        handleDepartmentGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('department.button.new'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('department.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: t('department.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('department.chipFilters.totalRecords'), value: departmentList.length },
        { label: t('department.chipFilters.name'), value: departmentFilterDTO?.name },
        { label: t('department.chipFilters.responsible'), value: responsibleList.find(x => x.id === departmentFilterDTO?.responsibleId)?.fullName },
        { label: t('department.chipFilters.company'), value: companyList.find(x => x.id === departmentFilterDTO?.companyId)?.name },

    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('department.columns.name'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'responsible', label: t('department.columns.responsible'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'company', label: t('department.columns.company'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title={t('department.listName')}>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label={t('department.filter.name')}
                                setValue={setDepartmentFilterDTO}
                                value={departmentFilterDTO?.name}
                                obj={departmentFilterDTO}
                                placeholder={t('department.filter.name')}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='companyId'
                                label={t('department.filter.company')}
                                setValue={setDepartmentFilterDTO}
                                value={departmentFilterDTO?.companyId}
                                obj={departmentFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                                searchable={true}
                                labelKey='name'
                                valueKey='id'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='responsibleId'
                                label={t('department.filter.responsible')}
                                setValue={setDepartmentFilterDTO}
                                value={departmentFilterDTO?.responsibleId}
                                obj={departmentFilterDTO}
                                valueList={responsibleList}
                                hasNoneSelect={true}
                                searchable={true}
                                labelKey='fullName'
                                valueKey='id'
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={departmentList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <DepartmentMaintenance
                        data={newRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <DepartmentMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'corpsphere.department'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};